import React, { useMemo, useState } from "react";
import FundReopenNotifications from "../fund-reopen-notifications/FundReopenNotifications";

function stripTagsToText(html) {
  return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function slugify(text) {
  var s = String(text || "");
  try {
    s = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  } catch (e) {}
  s = s
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
  return s;
}

const EligibilityChecklist = ({
  fundTitle,
  checklistTitle,
  items = [],            // array of HTML strings from ACF WYSIWYG
  name = "eligibility",   // base input name
  idPrefix = "elig",      // prefix for input IDs
  fundStatus,
  applyHref = "https://panapply.org/",
  // kept for compatibility (we use FundReopenNotifications instead of this link when closed)
  waitlistHref = "#waitlist",
  data
}) => {
  // Normalize items → [{ html, id, value }]
  const entries = useMemo(function () {
    var arr = Array.isArray(items) ? items : [];
    return arr.map(function (html, i) {
      var text = stripTagsToText(html);
      var base = slugify(text || ("item-" + i));
      var id = (idPrefix ? idPrefix + "-" : "") + base + "-" + i;
      return { html: String(html), id: id, value: i };
    });
  }, [items, idPrefix]);

  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaHref, setCtaHref] = useState("");
  const [showCheckButton, setShowCheckButton] = useState(true);
  const [inputsDisabled, setInputsDisabled] = useState(false); // lock checkboxes after evaluate
  const [isEligible, setIsEligible] = useState(null); // null = not evaluated, true/false after click

  // Real status flags
  const statusStr = String(fundStatus || '')
    .toLowerCase()
    .replace(/\u00a0/g, ' ') // NBSP → space
    .trim();

  const fundIsOpen = statusStr === 'open';
  
  const fundIsOpenToWaitlistOnly = statusStr === 'open to wait list only' || statusStr === 'wait list processing';

  const fundIsClosed =
    ! fundIsOpenToWaitlistOnly &&
    (/\bwait\W*list\b|\bclosed\b|\bexhaust(?:ed)?\b|\bpaused\b|\bsuspend(?:ed)?\b|\bon hold\b|\btemporar(?:il)?y closed\b/.test(statusStr));

  function handleToggle(val, checked) {
    if (inputsDisabled) return; // ignore changes while locked

    var next = selected.slice();
    var idx = next.indexOf(val);
    if (checked && idx === -1) next.push(val);
    if (!checked && idx !== -1) next.splice(idx, 1);
    setSelected(next);
  }

  function allChecked() {
    if (!entries.length) return false;
    for (var i = 0; i < entries.length; i++) {
      if (selected.indexOf(i) === -1) return false;
    }
    return true;
  }

  function handleEvaluate() {
    var ok = allChecked();
    setIsEligible(ok); // record outcome

    // reset CTA each time
    setCtaLabel("");
    setCtaHref("");

    if (!ok) {
      // Not all boxes checked → show message only (NO CTA of any kind)
      setMessage(
        "Based on your answers, you are not eligible at this time. If your eligibility changes, please check again. We invite you to explore patient support resources below."
      );
      setShowMessage(true);
      setShowCheckButton(false);
      setInputsDisabled(true);     // lock boxes after evaluation
      return;
    }

    // All boxes checked
    setMessage("Based on your answers, you are eligible for this grant! We invite you to apply or sign up for notifications, and to explore our patient support resources below.");

    if (fundIsOpen) {
      // Fund open + all checked → show Apply CTA
      setCtaLabel("Apply online");
      setCtaHref(applyHref);
    }
    // Fund closed + all checked → we will show <FundReopenNotifications /> below

    setShowMessage(true);
    setShowCheckButton(false);
    setInputsDisabled(true);       // lock boxes after evaluation
  }

  function handleReset(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setSelected([]);
    setMessage("");
    setShowMessage(false);
    setCtaLabel("");
    setCtaHref("");
    setShowCheckButton(true);
    setInputsDisabled(false);      // unlock boxes on reset
    setIsEligible(null);           // back to "not evaluated"
  }

  const isApplyCta = ctaHref === applyHref;

  return (
    <div className="u-richtext f-scale-body" aria-live="polite">
      <h3>{checklistTitle}</h3>
      <p>
        To get financial assistance for {fundTitle}, you must meet all of the
        criteria listed below. We recommend looking up your household income category first.
      </p>
      <p>
        <strong>Check the boxes below to see your eligibility results:</strong>
      </p>

      {/* Checkboxes */}
      {entries.length ? (
        <ul className="u-listReset" aria-disabled={inputsDisabled ? "true" : "false"}>
          {entries.map(function (entry) {
            var checked = selected.indexOf(entry.value) !== -1;
            return (
              <li key={entry.id} className="u-mb1">
                <input
                  id={entry.id}
                  type="checkbox"
                  name={name ? name + "[]" : "eligibility[]"}
                  className="EligibilityChecklist-input"
                  value={entry.value}
                  checked={checked}
                  disabled={inputsDisabled}
                  onChange={function (e) {
                    handleToggle(entry.value, e.target.checked);
                  }}
                />
                <label
                  htmlFor={entry.id}
                  className={"u-ml1" + (inputsDisabled ? " is-disabled" : "")}
                  aria-disabled={inputsDisabled ? "true" : "false"}
                >
                  <span dangerouslySetInnerHTML={{ __html: entry.html }} />
                </label>
              </li>
            );
          })}
        </ul>
      ) : (
        <p>No eligibility items configured.</p>
      )}

      {/* Button (hidden after evaluation until reset) */}
      {showCheckButton ? (
        <div className="check-eligibility-btn-container">
          <button
            type="button"
            className="btn"
            onClick={handleEvaluate}
            aria-describedby="eligibility-checklist-result"
          >
            Check eligibility
          </button>
        </div>
      ) : null}

      {/* Result + CTA/Waitlist + Reset */}
      <div
        id="eligibility-checklist-result"
        className="u-mt2"
        role="status"
      >
        <div className="eligibility-result-message-container">
          {showMessage && (
            <>
              <p>{message}</p>
              {fundIsOpenToWaitlistOnly && isEligible === true && (
                <p>Note: Our wait list process has been sunset.</p>
              )}
            </>
          )}

          {/* Show CTA ONLY if the last evaluation passed (all boxes checked) */}
          {showMessage && isEligible === true ? (
            <div className="u-mt1">
              {fundIsClosed || fundIsOpenToWaitlistOnly ? (
                // Fund closed → show wait list modal trigger (only when eligible)
                // <FundReopenNotifications data={data} />
                <a class="btn" href="https://totalassist.org/notify" target="_blank" rel="noopener">Get notified when this fund opens</a>
              ) : (
                // Fund open → show Apply CTA only when eligible and CTA set
                (! fundIsOpenToWaitlistOnly && ctaLabel && ctaHref) ? (
                  <a
                    href={ctaHref}
                    className="btn"
                    target={isApplyCta ? "_blank" : undefined}
                    rel={isApplyCta ? "noopener" : undefined}
                  >
                    {ctaLabel}
                  </a>
                ) : null
              )}
            </div>
          ) : null}
        </div>

        {/* Show Reset ONLY after an evaluation has been performed */}
        {showMessage ? (
          <p>
            <a href="#" onClick={handleReset}>Reset</a>
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default EligibilityChecklist;
